import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { DestroyRef, Injectable, DOCUMENT, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { Subject } from 'rxjs';

@Injectable()
export class DialogService {
  private document = inject<Document>(DOCUMENT);
  private dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly defaultDialogAriaLabel = 'Dialog';

  /**
   * Wraps material dialog.open to customize opening/closing animations.
   * Current solution implements css opening animation
   */
  open<T, D = any, R = any>(component: ComponentType<T>, config: MatDialogConfig<D> = {}): MatDialogRef<T, R> {
    const animationClass = 'fadeIn';

    const dialogRef = this.dialog.open(component, {
      ...config,
      ariaModal: true,
      autoFocus: config.autoFocus ?? 'dialog',
      delayFocusTrap: config.delayFocusTrap ?? false,
      restoreFocus: config.restoreFocus ?? true,
      scrollStrategy: new NoopScrollStrategy()
    });

    dialogRef.addPanelClass(animationClass);
    setTimeout(() => this.applyDialogAccessibility(dialogRef));

    const backdrop = document.querySelector('.cdk-overlay-backdrop') as HTMLElement;
    const container = document.querySelector('.mat-mdc-dialog-container') as HTMLElement;
    const wrapper = document.querySelector('.cdk-global-overlay-wrapper') as HTMLElement;

    const afterClosed = new Subject();
    dialogRef.afterClosed = () => afterClosed.asObservable();

    const closeFunction = dialogRef.close;
    const closeHandler = (dialogResult?: any) => {
      afterClosed.next(dialogResult);

      wrapper.style.display = 'none';
      backdrop.style.display = 'none';
      dialogRef.close = closeFunction;
      dialogRef.close(dialogResult);
    };
    dialogRef.close = (dialogResult?: any) => closeHandler(dialogResult);

    if (!config.disableClose) {
      container.addEventListener('click', e => e.stopPropagation());
      wrapper.addEventListener('click', () => closeHandler());
    }

    return dialogRef;
  }

  toggleOpenClassOnBodyListeners() {
    this.dialog.afterOpened.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(dialogRef => {
      this.document.body.classList.add('modal-open');
      this.applyDialogAccessibility(dialogRef);
    });

    this.dialog.afterAllClosed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.document.body.classList.remove('modal-open'));
  }

  private applyDialogAccessibility(dialogRef: MatDialogRef<any>) {
    const container = this.document.getElementById(dialogRef.id);

    if (!container) {
      return;
    }

    container.setAttribute('aria-modal', 'true');
    this.ensureAccessibleDialogName(container);
    this.makeCloseControlsKeyboardAccessible(container);
  }

  private ensureAccessibleDialogName(container: HTMLElement) {
    if (container.hasAttribute('aria-label') || container.hasAttribute('aria-labelledby')) {
      return;
    }

    const titleElement = container.querySelector<HTMLElement>(
      'h1, h2, h3, [mat-dialog-title], .modal_title, .modal-title'
    );

    if (titleElement?.textContent?.trim()) {
      if (!titleElement.id) {
        titleElement.id = `${container.id}-title`;
      }

      container.setAttribute('aria-labelledby', titleElement.id);
      return;
    }

    container.setAttribute('aria-label', this.defaultDialogAriaLabel);
  }

  private makeCloseControlsKeyboardAccessible(container: HTMLElement) {
    const closeElements = Array.from(container.querySelectorAll<HTMLElement>('.modal-close'));

    closeElements.forEach(closeElement => {
      const closeControl = this.getCloseControlElement(closeElement);
      this.decorateCloseControl(closeControl);
    });
  }

  private getCloseControlElement(closeElement: HTMLElement): HTMLElement {
    if (closeElement.tagName.toLowerCase() === 'button') {
      return closeElement;
    }

    return closeElement.querySelector<HTMLElement>('button, app-icon, [role="button"], a, .icon, i') ?? closeElement;
  }

  private decorateCloseControl(closeControl: HTMLElement) {
    if (!closeControl.hasAttribute('aria-label')) {
      closeControl.setAttribute('aria-label', 'Close');
    }

    if (closeControl.tagName.toLowerCase() !== 'button') {
      if (!closeControl.hasAttribute('tabindex')) {
        closeControl.setAttribute('tabindex', '0');
      }

      closeControl.setAttribute('role', 'button');
    }

    if (closeControl.dataset.modalCloseKeyboardBound === 'true') {
      return;
    }

    closeControl.dataset.modalCloseKeyboardBound = 'true';
    closeControl.onkeydown = event => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      closeControl.click();
    };
  }
}
